import * as React from "react";
import {
    Panel,
    Stack,
    IStackTokens,
    Label,
    ILabelStyles,
    FontSizes,
    FontWeights,
    Dropdown,
    TextField,
    IDropdownOption,
    IDropdownStyles,
    ResponsiveMode,
    PrimaryButton,
    DefaultButton,
    Persona,
    PersonaSize,
    Spinner,
    SpinnerSize,
    Icon,
    Link,
    TooltipHost,
    NormalPeoplePicker,
    IBasePickerSuggestionsProps,
    IPersonaProps
} from "@fluentui/react";
import { connect, ConnectedProps } from "react-redux";
import { LanguageContext } from "../../../../../utilities/LocalizationModule";
import { uploadPanelStyles } from "../../../config/controlStyles";
import { IApproval, IApprovalResponse, IReassignResponse } from "./ApprovalsModels";
import { ApprovalsService } from "./ApprovalsService";
import { RootState } from "../../../redux";
import { removeApproval } from "../../../redux/modules/approvals"
import { searchPeople, searchUsers } from "../../../services/GraphService";
import { DefaultAction, IApprovalAction } from "./IApprovalAction";
import { validateEmail } from "../../helpers/validateEmail";
import { IPerson } from "../../../interfaces/IPersonResult";
import { IGraphGetUsersResponse } from "../../_v2/redux/api/graph/api-graph-interfaces";

const verticalGapStackTokens: IStackTokens = {
    childrenGap: 15,
};

const dropdownStyles: Partial<IDropdownStyles> = {
    dropdown: { width: "100%" },
};

const boxContainer: React.CSSProperties = {
    marginTop: 10,
    border: "1px solid var(--db-border, rgb(220,220,220))",
    borderRadius: 3,
    boxShadow: "0px 2px 9px 1px rgba(160,160,160,0.15)",
}

const labelContainer: React.CSSProperties = {
    padding: "5px 15px",
    margin: 0,
    borderBottom: "1px solid var(--db-border, rgb(220,220,220))",
}

const contentContainer: React.CSSProperties = {
    padding: 15,
    margin: 0,
}

const boxTitleStyle: ILabelStyles = {
    root: {
        fontSize: FontSizes.mediumPlus,
        fontWeight: FontWeights.bold,
        margin: 0
    }
}

const sectionTitleStyle: ILabelStyles = {
    root: {
        fontSize: FontSizes.medium,
        fontWeight: FontWeights.semibold,
        margin: 0,
        padding: 0
    }
}

const textStyle: ILabelStyles = {
    root: {
        fontSize: FontSizes.size14,
        fontWeight: FontWeights.regular,
        margin: 0,
        padding: 0
    }
}

const linkStyle: ILabelStyles = {
    root: {
        fontSize: FontSizes.size14,
        fontWeight: FontWeights.regular,
        margin: 0,
        padding: 0,
        wrapFlow: "wrap",
        overflowWrap: "break-word"
    }
}

const responseStyle: React.CSSProperties = {
    padding: 15,
    width: "100%"
}

const errorStyle: React.CSSProperties = {
    ...responseStyle,
    backgroundColor: "var(--db-error-bg, #fde7e9)",
}

const errorLabel: ILabelStyles = {
    root: {
        fontSize: FontSizes.size14,
        fontWeight: FontWeights.regular,
        margin: 0,
        padding: 0,
        color: "var(--db-error-fg, #a80000)",
    }
}

const successStyle: React.CSSProperties = {
    ...responseStyle,
    backgroundColor: "var(--db-success-bg, #dff6dd)",
}

const successLabel: ILabelStyles = {
    root: {
        fontSize: FontSizes.size14,
        fontWeight: FontWeights.regular,
        margin: 0,
        padding: 0,
        color: "var(--db-success-fg, #107c10)",
    }
}

const getSecondaryTextFromPerson = (person: IPerson): string | undefined => {
    if (person?.scoredEmailAddresses?.length > 0 && validateEmail(person.scoredEmailAddresses[0]?.address)) {
        return person.scoredEmailAddresses[0].address;
    }
    if (validateEmail(person?.userPrincipalName)) {
        return person.userPrincipalName;
    }
    return undefined;
};

const getSecondaryTextFromUser = (user: IGraphGetUsersResponse): string | undefined => {
    if (validateEmail(user?.mail)) {
        return user.mail;
    }
    if (validateEmail(user?.userPrincipalName)) {
        return user.userPrincipalName;
    }
    return undefined;
};

const mapStateToProps = (state: RootState) => {
    return {
        tenantId: state.user?.account?.tenantId,
        userUniqueId: state.user?.account?.uniqueId
    };
};

const mapDispatchToProps = {
    removeApproval,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface IApprovalPanelProps extends PropsFromRedux {
    isApprovalsPanelOpen: boolean;
    dismissApprovalsPanel: () => void;
    approval: IApproval,
    passedAction: IApprovalAction,
    approvalActionList: IApprovalAction[]
}

const ApprovalsPanel: React.FunctionComponent<IApprovalPanelProps> = ({
    isApprovalsPanelOpen,
    dismissApprovalsPanel,
    approval,
    passedAction,
    tenantId,
    removeApproval,
    userUniqueId,
    approvalActionList
}) => {
    const languageStrings = React.useContext(LanguageContext);

    const [selectedActionDropDown, setSelectedActionDropDown] = React.useState<IDropdownOption>(null);
    const [commentText, setCommentText] = React.useState<string>("");
    const [currentAction, setCurrentAction] = React.useState<number>(DefaultAction.NONE);
    const [isResponseLoading, setIsResponseLoading] = React.useState<boolean>(false);
    const [approvalSuccess, setApprovalSuccess] = React.useState<boolean>(false);
    const [standardMode, setStandardMode] = React.useState<boolean>(true);
    const [reassignPerson, setReassignPerson] = React.useState<string>("");
    const [reassignErrorText, setReassignErrorText] = React.useState<string>("");

    const pickerRef = React.useRef<any>(null);

    const suggestionProps: IBasePickerSuggestionsProps = {
        suggestionsHeaderText: languageStrings.SuggestedPeople,
        mostRecentlyUsedHeaderText: languageStrings.SuggestedContacts,
        noResultsFoundText: languageStrings.NoResultsFound,
        loadingText: languageStrings.Loading,
        showRemoveButtons: true,
    };

    React.useEffect(() => {
        if (passedAction) {
            setCurrentAction(passedAction.id);
            setSelectedActionDropDown(dropdownOptions.find(option => option.key === passedAction.id));
        }
    }, [passedAction])

    const dropdownOptions: IDropdownOption[] = React.useMemo(() => {
        return approvalActionList.map(action => {
            return ({
                key: action.id,
                text: action.name,
            })
        })
    }, [approvalActionList])

    const resetPanel = () => {
        setCommentText("");
        setCurrentAction(DefaultAction.NONE);
        setIsResponseLoading(false);
        setApprovalSuccess(false);
        setStandardMode(true);
        setSelectedActionDropDown(null);
    }

    const elipsReplacer = (word: string, count: number): string => {
        const tmp = word.split("");
        tmp.splice(count, word.length - (count));
        return tmp.join("") + "...";
    }

    const onChangeDropDown = (option: IDropdownOption) => {
        setCurrentAction(approvalActionList.find(action => action.id === option.key).id)
        setReassignPerson("");
        setReassignErrorText("");
        setSelectedActionDropDown(option);
    }

    const onFilterChanged = React.useCallback(async (
        filterText: string,
        currentPersonas: IPersonaProps[]
    ): Promise<IPersonaProps[]> => {
        if (!filterText) {
            return [];
        }

        const [people, users] = await Promise.all([
            searchPeople(filterText, true),
            searchUsers(filterText)
        ]);

        if (people.length === 0 && users.length === 0) {
            return [];
        }

        const existingEmails = new Set<string>(
            currentPersonas.map(p => p.secondaryText?.toLowerCase()).filter(Boolean) as string[]
        );

        const candidates = [
            ...people.map(person => ({
                id: person.id,
                text: person.displayName,
                secondaryText: getSecondaryTextFromPerson(person),
            })),
            ...users.map(user => ({
                id: user.id,
                text: user.displayName,
                secondaryText: getSecondaryTextFromUser(user),
            }))
        ];

        const uniqueSuggestions: IPersonaProps[] = [];
        for (const candidate of candidates) {
            if (candidate.secondaryText) {
                const lowercasedSecondaryText = candidate.secondaryText.toLowerCase();
                if (!existingEmails.has(lowercasedSecondaryText)) {
                    uniqueSuggestions.push(candidate as IPersonaProps);
                    existingEmails.add(lowercasedSecondaryText);
                }
            }
        }

        const priorityValue = filterText.toLowerCase();
        uniqueSuggestions.sort((a, b) => {
            const aLowerText = a.text.toLowerCase();
            const aLowerSecondaryText = a.secondaryText.toLowerCase();
            const bLowerText = b.text.toLowerCase();
            const bLowerSecondaryText = b.secondaryText.toLowerCase();

            const aHasPriority = aLowerText.startsWith(priorityValue) || aLowerSecondaryText.includes(priorityValue);
            const bHasPriority = bLowerText.startsWith(priorityValue) || bLowerSecondaryText.includes(priorityValue);

            if (aHasPriority && !bHasPriority) return -1;
            if (!aHasPriority && bHasPriority) return 1;

            return aLowerText.localeCompare(bLowerText);
        });

        return uniqueSuggestions;
    }, []);

    const executeResponseAction = async () => {
        setIsResponseLoading(true);
        setStandardMode(false);
        const approvalService = new ApprovalsService(tenantId);
        let response: IApprovalResponse;
        response = await approvalService.respond(approval.name, commentText, approvalActionList.find(action => action.id === currentAction).name);
        if (response && response.properties.status === "Committed") {
            setApprovalSuccess(true);
            removeApproval(approval);
        } else {
            setApprovalSuccess(false);
        }
        setIsResponseLoading(false);
    }

    const executeReassignAction = async () => {
        setIsResponseLoading(true);
        setStandardMode(false);
        try {
            const approvalService = new ApprovalsService(tenantId);
            const response: IReassignResponse = await approvalService.reassign(approval.name, reassignPerson, userUniqueId);
            if (response) {
                setApprovalSuccess(true);
                removeApproval(approval);
                setReassignErrorText("");
            } else {
                setApprovalSuccess(false);
            }
            setIsResponseLoading(false);
        } catch (err) {
            setApprovalSuccess(false);
            setIsResponseLoading(false);
        }

    }

    function getTextFromItem(persona: IPersonaProps): string {
        return persona.text as string;
    }

    const onPeoplePickerListChange = (items?: IPersonaProps[]) => {
        if (items.length > 0) {
            const { secondaryText: mail, id } = items[0];
            const isAlreadyAssigned: boolean = approval.properties.approvers.some(approver => approver === id);
            const isAssignedToApprovalOwner: boolean = approval.properties.owner.id === id;
            if (isAlreadyAssigned) {
                setReassignErrorText(languageStrings.AlreadyAssigned)
            } else if (isAssignedToApprovalOwner) {
                setReassignErrorText(languageStrings.ApprovalRequestNotAllowedOwner)
            } else {
                setReassignErrorText("");
            }
            //we set it up that the secondary text is the email

            setReassignPerson(mail);
        } else {
            setReassignErrorText("");
            setReassignPerson("");
        }
    }

    const standardContent = (
        <Stack tokens={verticalGapStackTokens}>
            <Stack style={boxContainer}>
                <Stack style={labelContainer}>
                    <Label styles={boxTitleStyle}>
                        {languageStrings.Overview}
                    </Label>
                </Stack>
                <Stack style={contentContainer} tokens={{ childrenGap: 15 }}>
                    <Stack.Item>
                        <Label styles={sectionTitleStyle} >
                            {languageStrings.Approval}
                        </Label>
                        <Label styles={textStyle}>
                            {approval?.properties.title}
                        </Label>
                    </Stack.Item>
                    <Stack.Item>
                        <Label styles={sectionTitleStyle} >
                            {languageStrings.Requester}
                        </Label>
                        <Label styles={textStyle}>
                            <Persona
                                styles={{
                                    root: {
                                        marginTop: 5
                                    }
                                }}
                                size={PersonaSize.size24}
                                text={approval?.properties.principals[0].displayName}
                            />

                        </Label>
                    </Stack.Item>
                    <Stack.Item>
                        <Label styles={sectionTitleStyle} >
                            {languageStrings.Received}
                        </Label>
                        <Label styles={textStyle}>
                            {(new Date(approval?.properties.creationDate)).toLocaleDateString()}
                        </Label>
                    </Stack.Item>
                    {approval?.properties?.item?.link && <Stack.Item>
                        <Label styles={sectionTitleStyle} >
                            {languageStrings.Link}
                        </Label>
                        <TooltipHost content={approval.properties.item.link} style={{ width: 250 }}>
                            <Link
                                styles={linkStyle}
                                target="_blank"
                                href={approval.properties.item.link} underline>
                                {approval.properties.item.displayName ?? elipsReplacer(approval.properties.item.link, 32)}
                            </Link>
                        </TooltipHost>

                    </Stack.Item>
                    }
                </Stack>
            </Stack>

            {approval?.properties?.details && <Stack style={boxContainer}>
                <Stack style={labelContainer}>
                    <Label styles={boxTitleStyle}>
                        {languageStrings.Details}
                    </Label>
                </Stack>
                <Stack style={contentContainer} tokens={{ childrenGap: 15 }}>
                    <Stack.Item>
                        <Label styles={textStyle}>
                            {approval?.properties?.details}
                        </Label>
                    </Stack.Item>
                </Stack>
            </Stack>}
            <Dropdown
                options={dropdownOptions}
                selectedKey={selectedActionDropDown?.key ?? undefined}
                onChange={(_, option) => onChangeDropDown(option)}
                styles={dropdownStyles}
                placeholder={languageStrings.SelectAction}
                // responsiveMode={ResponsiveMode.small}
                label={languageStrings.SelectAction}
            />
            {currentAction !== DefaultAction.REASSIGN ?
                <TextField
                    multiline
                    rows={5}
                    value={commentText}
                    onChange={(_, nv) => setCommentText(nv)}
                    label={languageStrings.OptionalComment}
                    resizable={false}
                /> : currentAction === DefaultAction.REASSIGN ?
                    <Stack>
                        <Label>
                            {languageStrings.ReassignTo}
                        </Label>
                        <NormalPeoplePicker
                            onResolveSuggestions={onFilterChanged}
                            getTextFromItem={getTextFromItem}
                            pickerSuggestionsProps={suggestionProps}
                            key={"normal"}
                            //itemLimit={props.itemLimit}
                            removeButtonAriaLabel={"Remove"}
                            inputProps={{
                                placeholder: languageStrings.EnterEmail
                            }}
                            componentRef={pickerRef}
                            onBlur={(ev: React.FocusEvent<HTMLInputElement>) => {
                                try {
                                    pickerRef.current?.input.current._updateValue("");
                                } catch (error) { }
                            }}
                            onChange={onPeoplePickerListChange}
                            resolveDelay={300}
                            itemLimit={1}
                        // label={languageStrings.ReassignTo}
                        // placeholder={languageStrings.EnterEmail}
                        />
                        {reassignErrorText &&
                            <Label styles={errorLabel} style={{ marginTop: 5 }}>
                                {reassignErrorText}
                            </Label>}
                    </Stack>
                    : null
            }
        </Stack>
    )

    const loadingContent = (
        isResponseLoading
            ? <Spinner size={SpinnerSize.large} style={{ marginTop: 15 }} />
            :
            <Stack>
                {approvalSuccess ?
                    <Stack style={successStyle} horizontal verticalAlign="center" horizontalAlign="start">
                        <Icon iconName="CheckMark" styles={{ root: { color: "var(--db-success-fg, #107c10)", padding: 0, margin: 0, paddingRight: 15, fontSize: 18, fontWeight: "bold" } }} />
                        <Label styles={successLabel}>
                            {languageStrings.ActionSucceed}
                        </Label>
                    </Stack>
                    : <Stack style={errorStyle} horizontal verticalAlign="center" horizontalAlign="start">
                        <Icon iconName="Cancel" styles={{ root: { color: "var(--db-error-fg, #a80000)", padding: 0, margin: 0, paddingRight: 15, fontSize: 18, fontWeight: "bold" } }} />
                        <Label styles={errorLabel}>
                            {languageStrings.ActionFailed}
                        </Label>
                    </Stack>}
            </Stack>
    )

    const onRenderFooterContent = React.useCallback(
        () => (
            standardMode
                ? <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text={languageStrings.Confirm}
                        onClick={() => {
                            currentAction === DefaultAction.REASSIGN ? executeReassignAction() : executeResponseAction();
                        }}
                        disabled={currentAction === DefaultAction.NONE || (currentAction === DefaultAction.REASSIGN && !reassignPerson) || reassignErrorText !== ""} />
                    <DefaultButton text={languageStrings.Cancel} onClick={() => {
                        resetPanel();
                        dismissApprovalsPanel()
                    }} />
                </Stack>
                : !isResponseLoading &&
                <Stack horizontalAlign="end">
                    <PrimaryButton text={languageStrings.Done} onClick={() => {
                        resetPanel();
                        dismissApprovalsPanel();
                    }} />
                </Stack>
        ),
        [isResponseLoading, executeReassignAction, executeResponseAction, resetPanel, dismissApprovalsPanel],
    );

    return (
        <Panel
            headerText={languageStrings.ApprovalCenter}
            isOpen={isApprovalsPanelOpen}
            styles={uploadPanelStyles}
            onDismiss={() => {
                resetPanel();
                dismissApprovalsPanel()
            }}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {standardMode ? standardContent : loadingContent}
        </Panel>
    )
};

export default connector(ApprovalsPanel);